import { Autocomplete, Box, Button, CircularProgress, Grid, IconButton, MenuItem, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material'
import React, { useEffect, useState } from 'react'
import useDebounce from '../../../hooks/UseDebounce';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomModal from '../../CustomModal/CustomModal';
import CloseIcon from "@mui/icons-material/Close";
import { autoFocusOffWithOutBorder, tableCellNewPrescription } from '../../../style/PatinetFormStyle';
import Select, { components } from "react-select";
import { menuItemStyle } from '../../../style/AppointmentStyle';
import UseFetchTestShortCuts from '../../../helper/UseFetchTestShortCuts';
import { primaryBtnTeal, rotateSpinner } from '../../../style/PatientDetailsStyle';
import CreatableSelect from "react-select/creatable";
import { useQuery } from '@tanstack/react-query';
import { getLabsList, getMedicalTest } from '../../../connections/getApi';
import { showToast } from '../../../utils/toastUtil';
import { postShortCode } from '../../../connections/postApi';
import ModalDelete from './ModalDelete';
import { deleteShortCode } from '../../../connections/deleteApi';
import { updateTestShortCode } from '../../../connections/putApi';
import AddTestShortCode from './TestShortCode.jsx/AddTestShortCode';
import { tableRowLab } from '../../../style/LapPageStyle';
import EditTestShortCode from './TestShortCode.jsx/EditTestShortCode';


const TestShortCuts = () => {
    const isMdUp = useMediaQuery("(min-width:450px)");
    const visiblePageCount = isMdUp ? 3 : 5;
    const [page, setPage] = useState(1);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [medicineSearchInput, setMedicineSearchInput] = useState(null);
    const [newMedicine, setNewMedicine] = useState(false);
    const [detailsModal, setDetailsModal] = useState(false);
    // const [lastPage, setLastPage] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [errors, setErrors] = useState(true);
    const [medicalTestRows, setMedicalTestRows] = useState([]);
    const [id, setId] = useState(null);
    const [formState, setFormState] = useState({
        name: "",
    });
    const [isTyping, setIsTyping] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [shortCode, setShortCode] = useState({});

    const debouncedInputMedicine = useDebounce(medicineSearchInput, 1000);
    const { data: testShortCodeList, refetch, isLoading, isFetching } = UseFetchTestShortCuts(page, itemPerPage, debouncedInputMedicine);

    const totalRecords = testShortCodeList?.meta?.total_count || 0;
    const totalPage = Math.ceil(totalRecords / itemPerPage);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleChangeRow = (index, field, value, isNew) => {
        const updatedRows = [...medicalTestRows];
        updatedRows[index] = { ...updatedRows[index], [field]: value, ...(isNew !== undefined && { isNew }) };
        setMedicalTestRows(updatedRows);
    };

    const [medicalInput, setMedicalInput] = useState("");
    const handleMedicalChange = (val) => {
        setIsTyping(true);
        setMedicalInput(val);
    };

    const handleAddRow = () => {
        setMedicalTestRows((prev) => [...prev, { medical_test_id: null, lab: null }]);
    };

    const handleRemoveRow = (idx) => {
        const filtered = medicalTestRows.filter((_, i) => i !== idx);
        setMedicalTestRows(filtered);
    };

    const debounced = useDebounce(medicalInput, 700);

    const { data: medicalData, isFetching: loadingMedical } = useQuery({
        queryKey: ["medicalTests", debounced],
        queryFn: () => getMedicalTest(1, 10, null, debounced),
        enabled: !!debounced,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const [allMedicalTests, setAllMedicalTests] = useState([]);

    const medicalTests = medicalData?.payload?.medical_tests || [];
    const formattedMedicalOptions = medicalTests.map((t) => ({ value: t.id, label: t.name }));

    useEffect(() => {
        if (!formattedMedicalOptions?.length) return;

        setAllMedicalTests((prev) => {
            const merged = [...prev];

            formattedMedicalOptions.forEach((item) => {
                if (!prev.some((p) => p.value === item.value)) {
                    merged.push(item);
                }
            });

            return merged;
        });

    }, [medicalData, formattedMedicalOptions]);

    const { data: labData, isFetching: loadingLabs } = useQuery({
        queryKey: ["labs"],
        queryFn: () => getLabsList("all", null, null),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const formattedLabOptions = (labData?.payload?.labs || []).map((l) => ({ value: l.id, label: l.name }));


    const handleAddShortCode = async (e) => {
        e.preventDefault();

        if (!formState.name) {
            showToast("Name is required", "ERROR");
            return;
        }

        for (let i = 0; i < medicalTestRows.length; i++) {
            const row = medicalTestRows[i];

            // Check if medical test is selected
            if (!row.medical_test_id) {
                showToast(`Row ${i + 1}: Please select or create a medical test`, "ERROR");
                return;
            }

            // If it's a new test, check if lab is selected
            const isNewTest = typeof row.medical_test_id.value === "string";
            if (isNewTest && !row.lab) {
                showToast(`Row ${i + 1}: Please select a lab for the new test`, "ERROR");
                return;
            }
        }

        // Process medical test rows
        const existingTestIds = [];
        const newMedicalTests = [];

        medicalTestRows.forEach((row) => {
            if (row.medical_test_id) {
                const testValue = row.medical_test_id.value;
                const testLabel = row.medical_test_id.label;
                const isNewTest = typeof testValue === "string";

                if (isNewTest) {
                    const newTest = {
                        name: testLabel,
                        code: testValue.toUpperCase(),
                    };
                    // Add lab_id if lab is selected
                    if (row.lab) {
                        newTest.lab_id = row.lab.value;
                    }
                    newMedicalTests.push(newTest);
                } else {
                    existingTestIds.push(testValue);
                }
            }
        });

        const payload = {
            medical_test_shortcode: {
                name: formState?.name,
                medical_test_ids: existingTestIds.map(id => String(id)),
            },
            new_medical_tests: newMedicalTests,
        };

        try {
            const data = await postShortCode(payload);
            if (data.status.status === "SUCCESS") {
                showToast("Medical Short Code Added Successfully", "SUCCESS");
                setFormState({});
                setMedicalTestRows([]);
                refetch();
                setNewMedicine(false)
            }
        } catch (error) {
            // toast.error(error.message);
            console.log(error);
        }
    };

    const handleDelete = async (id) => {
        setDeleteModal(false);
        const res = await deleteShortCode(id);
        showToast(res?.status?.message || res?.status?.status, res?.status?.status);
        if (res?.status?.status) {
            refetch(page, itemPerPage);
        }
    };

    const handleEdit = (code) => {
        setShortCode(code);
        setIsEdit(true);

        // setNewMedicine(true);
        // setId(code?.id);

        // Pre-fill name

    };

    const handleUpdateShortCode = async () => {
        //  e.preventDefault();

        if (!formState.name) {
            showToast("Name is required", "ERROR");
            return;
        }

        for (let i = 0; i < medicalTestRows.length; i++) {
            const row = medicalTestRows[i];

            // Check if medical test is selected
            if (!row.medical_test_id) {
                showToast(`Row ${i + 1}: Please select or create a medical test`, "ERROR");
                return;
            }

            // If it's a new test, check if lab is selected
            const isNewTest = typeof row.medical_test_id.value === "string";
            if (isNewTest && !row.lab) {
                showToast(`Row ${i + 1}: Please select a lab for the new test`, "ERROR");
                return;
            }
        }

        // Process medical test rows
        const existingTestIds = [];
        const newMedicalTests = [];

        medicalTestRows.forEach((row) => {
            if (row.medical_test_id) {
                const testValue = row.medical_test_id.value;
                const testLabel = row.medical_test_id.label;
                const isNewTest = typeof testValue === "string";

                if (isNewTest) {
                    const newTest = {
                        name: testLabel,
                        code: testValue.toUpperCase(),
                    };
                    // Add lab_id if lab is selected
                    if (row.lab) {
                        newTest.lab_id = row.lab.value;
                    }
                    newMedicalTests.push(newTest);
                } else {
                    existingTestIds.push(testValue);
                }
            }
        });

        const payload = {
            medical_test_shortcode: {
                name: formState?.name,
                medical_test_ids: existingTestIds.map(id => String(id)),
            },
            new_medical_tests: newMedicalTests,
        };

        try {
            const result = await updateTestShortCode(payload, id);
            showToast(result?.status?.message || result?.status?.status, result?.status?.status);
            if (result?.status?.status === "SUCCESS") {
                setNewMedicine(false);
                setIsEdit(false);
                setFormState({})
                refetch();
            }
        } catch (err) {
            showToast(err.message, "ERROR");
        } finally {
            setLoading(false);
        }
    };

    if (newMedicine) {
        return (
            <AddTestShortCode refetch={refetch} onClose={() => setNewMedicine(false)} />
        )
    }

    if (isEdit) {
        return (
            <EditTestShortCode refetch={refetch} onClose={() => setIsEdit(false)} shortCode={shortCode} setShortCode={setShortCode} setIsEdit={setIsEdit} />
        )
    }

    return (
        <Box sx={{ width: "100%", padding: '10px', backgroundColor: '#FFFFFF', px: 1 }}>
            <Box
                mt={1}
                mb={0}
                marginLeft={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                position="sticky"
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, alignItems: 'center' }} >
                    <Box>
                        <Typography variant='title_medium' sx={{ fontSize: '18px' }}>Test Shortcodes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'center' }} >
                        <AddRoundedIcon onClick={() => setNewMedicine(true)} sx={{
                            backgroundColor: newMedicine ? "neutral.tealDark" : "#FFFFFF", p: '4px', width: "24px", color: newMedicine ? "#FFFFFF" : "neutral.teal", borderRadius: "10px", cursor: "pointer", border: '2px solid #007A99',
                            transition: "0.3s ease",
                            "&:hover": {
                                backgroundColor: "#007A99", // teal
                                color: "#FFFFFF",
                            }
                        }} />

                        <IconButton
                            onClick={() => refetch()}
                            sx={{
                                backgroundColor: "#FFFFFF",
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                cursor: "pointer",
                                border: "2px solid #007A99",
                                transition: "0.3s ease",

                                "&:hover": {
                                    backgroundColor: "neutral.teal",
                                },
                            }}
                        >
                            <CachedOutlinedIcon
                                sx={{
                                    width: "22px",
                                    height: "22px",
                                    color: "neutral.teal",
                                    transition: "0.3s ease",

                                    // white color on parent hover
                                    ".MuiIconButton-root:hover &": {
                                        color: "#FFFFFF",
                                    },
                                    // animation if loading
                                    animation: isFetching ? `${rotateSpinner} 0.9s linear infinite` : "none",
                                }}
                            />
                        </IconButton>

                        {/* <FilterAltOutlinedIcon
                                                                                      // onClick={() => setNewMedicine(true)} 
                                                                                      sx={{ backgroundColor: "#FFFFFF", p: '4px', width: "24px", color: "neutral.teal", borderRadius: "10px", cursor: "pointer", border: '2px solid #007A99' }} /> */}
                    </Box>

                </Box>
            </Box>
            <Grid item xs={12} mt={3}>
                <TableContainer sx={{ mt: 1, width: "100%" }}>
                    <Table sx={{ width: "100%" }}>
                        <TableHead stickyHeader>
                            <TableRow
                                sx={{
                                    alignItems: 'center',
                                    "& th": {
                                        backgroundColor: "#EDEDED",
                                    },
                                }}
                            >
                                <TableCell sx={{ borderTopLeftRadius: '16px' }}>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        ID
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Title
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Medical Tests
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Created at
                                    </Typography>
                                </TableCell>

                                <TableCell >
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Updated at                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ borderTopRightRadius: '16px' }}>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Actions
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ p: 0 }}>
                                        <Box sx={{ rowGap: 1.2, display: 'flex', flexDirection: 'column' }}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((item, index) =>
                                                <Skeleton key={index}
                                                    variant="rectangular"
                                                    sx={{
                                                        width: "100%",
                                                        height: "50px",
                                                        backgroundColor: "#f0f0f0",
                                                    }}
                                                />
                                            )}

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                            :
                            (
                                <TableBody>
                                    {testShortCodeList?.payload?.medical_test_shortcodes?.map((shortcode, index) =>
                                        <TableRow key={index} sx={{
                                            ...tableRowLab, textAlign: "left", backgroundColor: "neutral.white",
                                            "& td:last-of-type": {
                                                borderTopRightRadius: "12px",
                                                borderBottomRightRadius: "12px",
                                                textAlign: "right",
                                            },
                                        }}>
                                            <TableCell
                                                sx={{
                                                    ...tableCellNewPrescription,
                                                    borderTopLeftRadius: '20px',
                                                    borderBottomLeftRadius: '20px',
                                                }}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {shortcode?.id} </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography
                                                    variant="subTitle116px"
                                                    sx={{
                                                        cursor: "pointer",
                                                        display: "inline-block",
                                                        py: 1,
                                                    }}
                                                >
                                                    {shortcode?.name}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography
                                                    variant="subTitle116px"
                                                    sx={{
                                                        color: "#3A3A3A",
                                                        py: 1,
                                                    }}
                                                >
                                                    {shortcode?.medical_tests?.map((test_item) =>
                                                        test_item?.name)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {shortcode?.created_at}</Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {shortcode?.updated_at}</Typography>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...tableCellNewPrescription,
                                                    width: '30px',
                                                    borderTopRightRadius: "20px",
                                                    borderBottomRightRadius: '20px',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', gap: '1px' }}>
                                                    <IconButton onClick={() => handleEdit(shortcode)}>
                                                        <EditNoteOutlinedIcon
                                                            sx={{ width: "24px", color: '#4A4A4A', }} />
                                                    </IconButton>
                                                    <IconButton onClick={() => {
                                                        setDeleteModal(true);
                                                        setId(shortcode?.id);
                                                    }} >
                                                        <DeleteForeverOutlinedIcon
                                                            sx={{ width: "24px", color: '#FB3748', }}
                                                        />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            )}
                    </Table>
                </TableContainer>
            </Grid>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0px",
                    backgroundColor: "#fff",
                    flexWrap: 'wrap'
                }}
            >

                <Typography fontSize="14px">
                    Showing {(page - 1) * itemPerPage + 1} -
                    {Math.min(page * itemPerPage, totalRecords)} of {totalRecords} entries
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>

                    {/* Previous Button */}
                    <Button
                        onClick={() => page > 1 && setPage(page - 1)}
                        startIcon={<ArrowBackIcon sx={{ fontSize: "14px" }} />}
                        disabled={page === 1}
                        sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            color: page === 1 ? "#9e9e9e" : "#007A99",
                            "&:disabled": { color: "#9e9e9e" }
                        }}
                    >
                        Previous
                    </Button>

                    {/* PAGE NUMBER BUTTONS */}
                    {[...Array(totalPage)].slice(0, visiblePageCount).map((_, idx) => {
                        const pageNum = idx + 1;
                        return (
                            <Button
                                key={pageNum}
                                onClick={() => setPage(pageNum)}
                                sx={{
                                    minWidth: "30px",
                                    height: "30px",
                                    fontSize: "12px",
                                    borderRadius: "6px",
                                    color: page === pageNum ? "#fff" : "#333",
                                    backgroundColor: page === pageNum ? "#007A99" : "#fff",
                                    border: "1px solid #d0d0d0",
                                    "&:hover": {
                                        backgroundColor: page === pageNum ? "#336CFB" : "#f2f2f2"
                                    }
                                }}
                            >
                                {pageNum}
                            </Button>
                        );
                    })}


                    {page > 5 &&
                        <Button
                            // onClick={() => setPage(pageNum)}
                            sx={{
                                minWidth: "30px",
                                height: "30px",
                                fontSize: "12px",
                                borderRadius: "6px",
                                color: "#fff",
                                backgroundColor: "#007A99",
                                border: "1px solid #d0d0d0",
                                "&:hover": {
                                    backgroundColor: "#007A99",
                                }
                            }}
                        >
                            {page}
                        </Button>
                    }



                    {/* Next Button */}
                    <Button
                        onClick={() => page < totalPage && setPage(page + 1)}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: "14px" }} />}
                        disabled={page === totalPage}
                        sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            color: page === totalPage ? "#9e9e9e" : "#007A99",
                            "&:disabled": { color: "#9e9e9e" }
                        }}
                    >
                        Next
                    </Button>

                    {/* Last Button */}
                    {page !== totalPage &&
                        <Button
                            onClick={() => setPage(totalPage)}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#336CFB"
                            }}
                        // disabled={page === totalPage}
                        >
                            Last &gt;&gt;
                        </Button>
                    }

                    {/* First  Button */}

                    {page !== 1 &&
                        <Button
                            onClick={() => setPage(1)}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#336CFB"
                            }}
                            disabled={page === 1}
                        >
                            &lt;&lt;  First
                        </Button>
                    }

                </Box>
            </Box>

            {/* modals */}
            <CustomModal
                customStyles={{ border: "none", width: '85%', marginTop: '40px' }}
                open={newMedicine}
                onClose={() => {
                    setNewMedicine(false);
                    setFormState({});
                    setMedicalTestRows([]);
                    setIsEdit(false);
                }}
            >
                <Box sx={{
                    p: 2, overflowY: 'auto', maxHeight: "80vh", '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    '-ms-overflow-style': 'none',
                    'scrollbar-width': 'none',
                }}>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={4}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: "8px" }} >
                            <Typography
                                variant="title_medium"
                                gutterBottom
                                sx={{ fontSize: '20px' }}
                            >
                                {isEdit ? 'Update Test ShortCode' : 'Add Test Shortcode'}
                            </Typography>
                        </Box>
                        <IconButton sx={{ marginBottom: "r0em" }} onClick={() => {
                            setNewMedicine(false)
                            setFormState({});
                            setMedicalTestRows([]);
                            setIsEdit(false);
                        }}>
                            <CloseIcon size="small" sx={{ color: "red" }} />
                        </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item sm={4}>
                            <TextField value={formState?.name} fullWidth placeholder="Name" name="name" onChange={(e) => handleFormChange(e)} sx={{
                                ...autoFocusOffWithOutBorder,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "16px",
                                    height: "50px",
                                    padding: "9px !important",
                                    "& input": {
                                        padding: "8px",
                                        fontSize: "14px",
                                    },
                                    "& .MuiAutocomplete-popupIndicator": {
                                        display: "none",
                                    },
                                },
                            }}
                                error={!!errors['name']}
                                helperText={errors['name']}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                            <Typography variant="h6">Add Medical Tests</Typography>
                        </Grid>

                        {medicalTestRows.map((row, index) => (
                            <React.Fragment key={index}>
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" gap={2} alignItems="center">
                                        <Typography>{index + 1}</Typography>
                                        <Box flex={1}>
                                            <CreatableSelect
                                                isLoading={loadingMedical}
                                                options={allMedicalTests}
                                                value={row?.medical_test_id}
                                                onInputChange={handleMedicalChange}
                                                onChange={(option) => handleChangeRow(index, "medical_test_id", option, option?.__isNew__)}
                                                placeholder="Choose medical test"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        zIndex: 9999, // Set your desired z-index here
                                                        fontFamily: 'lato',
                                                    }),
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 999999,
                                                    }),
                                                    control: (baseStyles, state) => ({
                                                        ...baseStyles,
                                                        borderColor: state.isFocused
                                                            ? "#2e37a4"
                                                            : "rgba(46, 55, 164, 0.1)",
                                                        boxShadow: state.isFocused
                                                            ? "0 0 0 1px #2e37a4"
                                                            : "none",
                                                        "&:hover": {
                                                            borderColor: "rgba(46, 55, 164, 0.1)",
                                                        },
                                                        fontFamily: 'lato',
                                                        borderRadius: "10px",
                                                        fontSize: "14px",
                                                        minHeight: "50px",
                                                    }),
                                                    dropdownIndicator: (base, state) => ({
                                                        ...base,
                                                        transform: state.selectProps.menuIsOpen
                                                            ? "rotate(-180deg)"
                                                            : "rotate(0)",
                                                        transition: "250ms",
                                                        width: "35px",
                                                        height: "35px",
                                                    }),
                                                }} />
                                        </Box>
                                    </Box>
                                </Grid>


                                {row.isNew && (
                                    <Grid item xs={12} md={5}>
                                        <Select
                                            options={formattedLabOptions}
                                            value={row.lab}
                                            onChange={(option) => handleChangeRow(index, "lab", option)}
                                            placeholder="Choose lab"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999, // Set your desired z-index here
                                                    fontFamily: 'lato',
                                                }),
                                                menuPortal: (base) => ({
                                                    ...base,
                                                    zIndex: 999999,
                                                }),
                                                control: (baseStyles, state) => ({
                                                    ...baseStyles,
                                                    borderColor: state.isFocused
                                                        ? "#2e37a4"
                                                        : "rgba(46, 55, 164, 0.1)",
                                                    boxShadow: state.isFocused
                                                        ? "0 0 0 1px #2e37a4"
                                                        : "none",
                                                    "&:hover": {
                                                        borderColor: "rgba(46, 55, 164, 0.1)",
                                                    },
                                                    fontFamily: 'lato',
                                                    borderRadius: "10px",
                                                    fontSize: "14px",
                                                    minHeight: "50px",
                                                }),
                                                dropdownIndicator: (base, state) => ({
                                                    ...base,
                                                    transform: state.selectProps.menuIsOpen
                                                        ? "rotate(-180deg)"
                                                        : "rotate(0)",
                                                    transition: "250ms",
                                                    width: "35px",
                                                    height: "35px",
                                                }),
                                            }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} md={1}>
                                    <IconButton color="error"
                                        onClick={() => handleRemoveRow(index)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Grid>
                            </React.Fragment>
                        ))}


                        <Grid item xs={12} mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ ...primaryBtnTeal, width: "20%", height: "45px", borderRadius: '30px' }}
                                onClick={handleAddRow}
                            >
                                Add New Item
                            </Button>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            marginTop: 3,
                            px: 1,
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end"   // <-- This aligns the button to the right
                        }}
                    >
                        <Button sx={{ ...primaryBtnTeal, paddingY: "26px", paddingX: '32px' }}
                            onClick={isEdit ? handleUpdateShortCode : handleAddShortCode}
                        >
                            <Typography variant='heading2_14' color={'neutral.white'}>
                                {isEdit ? 'Update' : 'Add'}</Typography>
                        </Button>
                    </Box>

                </Box>
            </CustomModal >
            <ModalDelete deleteModal={deleteModal} setDeleteModal={setDeleteModal} handleDelete={handleDelete} id={id} label={'Shortcode'} />
        </Box >
    )
}

export default TestShortCuts;

